import { cloneDeep } from 'lodash';
import React, { Dispatch, SetStateAction } from 'react';
import useDeployedMerchantRules from 'Hooks/query-hooks/single-merchant/useDeployedMerchantRules';
import MerchantRulesTable from 'Components/MerchantRulesTable';
import { Button } from '@brighterion/br-ui';
import useGetRuleColumns from 'Hooks/useGetRuleColumns';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line max-len
import MerchantOverview from 'Routes/Overrides/routes/MerchantOverrides/routes/MerchantDeployed/components/MerchantDeployedOverview';
import errorController from 'Components/ErrorScreens';
import { handleKeyEvent, handleOnClickEvent } from 'Utils';
import RouteLoading from 'Components/RouteLoading';
import styles from './index.css';

type SingleMerchantViewOptions = {
    fetch: boolean;
};

type Props = {
    rulesData: GlobalRulesData;
    merchantId: string;
    version: number;
    setShowSingleMerchant: Dispatch<SetStateAction<boolean>>;
    options?: Partial<SingleMerchantViewOptions>;
};

const SingleMerchantView = ({ rulesData, merchantId, version, setShowSingleMerchant, options }: Props) => {
    const { t } = useTranslation();
    const deployedMerchantRulesAPI = useDeployedMerchantRules({ merchantId, version, enabled: options?.fetch });
    const merchantRuleColumnNames = useGetRuleColumns({
        columns: [
            'modifiedFlag',
            {
                Header: () => null,
                id: 'overrideGroup',
                disableSortBy: true,
                accessor: (d) => {
                    return `${d?.overrideGroup}`;
                },
                groupByOrder: {
                    [t('Merchant Overrides')]: 0,
                    [t('Global Rules')]: 1
                }
            },
            'ruleId',
            'inputDescription',
            'conclusion',
            'override',
            'lastModified',
            'status'
        ]
    });

    const showSingleMerchant = () => {
        setShowSingleMerchant(false);
    };

    let merchantRules: GlobalRulesData;
    if (!options?.fetch) {
        merchantRules = cloneDeep(rulesData);
        merchantRules.rules.forEach((rule) => {
            rule._formatted = {
                ...rule._formatted,
                inputDescription: rule.inputDescription,
                outputDescription: rule.outputDescription,
                active: rule.parameters.active ? 'Active' : 'Inactive'
            };
        });
    } else {
        const { hasError, errorCmp } = errorController([deployedMerchantRulesAPI.error]);
        if (hasError) return <>{errorCmp}</>;
        if (deployedMerchantRulesAPI.isLoading || deployedMerchantRulesAPI.isFetching) {
            return <RouteLoading />;
        }
        merchantRules = deployedMerchantRulesAPI.data!;
    }

    return (
        <div className={styles.rulesContainer}>
            <Button
                className={styles.merchantBtn}
                variant='tertiary'
                size='small'
                iconName='arrow-left-1'
                onClick={handleOnClickEvent(showSingleMerchant)}
                onKeyDown={handleKeyEvent(showSingleMerchant)}
            >
                {t('Back to Merchant List')}
            </Button>
            <MerchantOverview
                merchantRules={merchantRules}
                leftTitle={t('merchantOverrideBreadcrumb', { mid: merchantId })}
            />
            <MerchantRulesTable
                deployedVersion={version}
                merchantId={merchantId}
                tableName='revision-window-merchant-rules'
                globalRules={rulesData!}
                merchantRules={merchantRules}
                columnNames={merchantRuleColumnNames}
                mode='revisionWindow'
            />
        </div>
    );
};

export default SingleMerchantView;
